import {
  formatCountOfTotal,
  formatPercentDisplay,
  normalizeMetric,
} from '../Dashboard/utils/metricFormatters';

export const EMPTY_PLACEHOLDER = '—';

const ZERO_COUNT_LINE = formatCountOfTotal(0, 0);

const performanceStage = (metrics, key) => normalizeMetric(metrics?.performance?.[key]);

/**
 * Maps wingman-metrics API into performance trend cards for Wingman Intelligence.
 */
export const buildPerformanceTrendCards = (metrics) => {
  const introToChat = performanceStage(metrics, 'introToChatOpenRate');
  const messageActivation = performanceStage(metrics, 'firstMessageRate');
  const reply = performanceStage(metrics, 'replyRate');
  const meaningful = performanceStage(metrics, 'deepConvoRate');

  const timeMetric = metrics?.performance?.timeToFirstMeaningfulConversation;
  const timeStr =
    timeMetric?.displayValue != null && String(timeMetric.displayValue).trim() !== ''
      ? String(timeMetric.displayValue)
      : EMPTY_PLACEHOLDER;

  const countLineOrZero = (stat) =>
    stat ? formatCountOfTotal(stat.count, stat.totalCount) : ZERO_COUNT_LINE;

  return [
    {
      key: 'intro-chat',
      title: 'Intro → Chat Open Rate',
      displayValue: introToChat ? formatPercentDisplay(introToChat.percentage) : '0%',
      countLine: countLineOrZero(introToChat),
    },
    {
      key: 'first-message',
      title: 'First Message Rate',
      displayValue: messageActivation ? formatPercentDisplay(messageActivation.percentage) : '0%',
      countLine: countLineOrZero(messageActivation),
    },
    {
      key: 'time-meaningful',
      title: 'Time to First Meaningful Conversation',
      displayValue: timeStr,
      countLine: timeMetric ? countLineOrZero(normalizeMetric(timeMetric)) : ZERO_COUNT_LINE,
    },
    {
      key: 'reply',
      title: 'Reply Rate',
      displayValue: reply ? formatPercentDisplay(reply.percentage) : '0%',
      countLine: countLineOrZero(reply),
    },
    {
      key: 'deep-convo',
      title: 'Deep Convo (8+ msgs)',
      displayValue: meaningful ? formatPercentDisplay(meaningful.percentage) : '0%',
      countLine: countLineOrZero(meaningful),
    },
  ];
};

const EVENT_FUNNEL_CONFIG = [
  { name: 'Event Suggested', key: 'eventSuggested', fill: '#42A5F5' },
  { name: 'Event Viewed', key: 'eventViewed', fill: '#42A5F5' },
  { name: 'RSVP', key: 'rsvp', fill: '#66BB6A' },
  { name: 'Attended', key: 'attended', fill: '#66BB6A' },
];

export const buildEventFunnelStages = (metrics) => {
  const raw = metrics?.eventFunnel;

  return EVENT_FUNNEL_CONFIG.map((cfg) => {
    const s = raw ? normalizeMetric(raw[cfg.key]) : null;
    return {
      name: cfg.name,
      count: s?.count ?? 0,
      percentage: Math.round(Number(s?.percentage) || 0),
      fill: cfg.fill,
    };
  });
};

/** Conversation funnel bar colors by percentage: 0–19 red, 20–59 yellow, 60–100 green. */
export const getConversationFunnelFillColor = (percentage) => {
  const p = Number(percentage) || 0;
  if (p < 20) return '#E53935';
  if (p < 60) return '#FDD835';
  return '#43A047';
};

const toConversationFunnelStage = ({ name, count, percentage }) => {
  const roundedPct = Math.round(Number(percentage) || 0);
  return {
    name,
    count,
    percentage: roundedPct,
    fill: getConversationFunnelFillColor(roundedPct),
  };
};

const CONVERSATION_FUNNEL_CONFIG = [
  { name: 'Wingman Intro Sent', key: 'introSent' },
  { name: 'Viewed Intro', key: 'viewedIntro' },
  { name: 'Accepted Intro', key: 'acceptedIntro' },
  { name: 'Requested Intro', key: 'requestedIntro' },
  { name: 'Skipped Intro', key: 'skippedIntro' },
  { name: 'Opened Chat', key: 'openedChat' },
  { name: 'First Message Sent', key: 'firstMessageSent' },
  { name: 'Reply Received', key: 'replyReceived' },
  { name: '8+ Messages (Deep)', key: 'deepConversation' },
  { name: 'Event RSVP', key: 'eventRsvp' },
];

/** Maps wingman-metrics `wingmanFunnel` into funnel chart stages; missing values are 0. */
export const buildConversationFunnelStages = (metrics) => {
  const funnel = metrics?.wingmanFunnel;

  return CONVERSATION_FUNNEL_CONFIG.map((cfg) => {
    const raw = funnel?.[cfg.key];
    const s = raw != null ? normalizeMetric(raw) : null;

    return toConversationFunnelStage({
      name: cfg.name,
      count: s?.count ?? 0,
      percentage: s?.percentage ?? 0,
    });
  });
};

/**
 * Splits conversation funnel for Apex: main funnel (descending %) + Event RSVP
 * (conversion metric — % can exceed prior step, breaks isFunnel if combined).
 */
export const buildConversationFunnelChartModel = (metrics) => {
  const all = buildConversationFunnelStages(metrics);
  const trailingStage = all.find((s) => s.name === 'Event RSVP') ?? null;
  const funnelStages = all.filter((s) => s.name !== 'Event RSVP');
  return { funnelStages, trailingStage };
};

/** @deprecated Use buildConversationFunnelStages — Recharts bar shape */
export const buildConversationFunnelData = (metrics) =>
  buildConversationFunnelStages(metrics).map((s) => ({
    name: s.name,
    value: s.percentage,
    fill: s.fill,
  }));

/** Health badge placeholder until a dedicated health API exists. */
export const computeHealthSummary = () => ({
  label: EMPTY_PLACEHOLDER,
  score: null,
  detail: EMPTY_PLACEHOLDER,
});

const POOL_HEALTH_CONFIG = [
  { key: 'intro-fatigue', label: 'Intro Fatigue', type: 'single' },
  { key: 'premium-saturation', label: 'Premium Saturation', type: 'single' },
  { key: 'stale-users', label: 'Stale Users (>14d inactive)', type: 'single' },
  { key: 'gender-imbalance', label: 'Gender Imbalance', type: 'split' },
];

const EMPTY_POOL_ROW = {
  statusLabel: EMPTY_PLACEHOLDER,
  statusDetail: EMPTY_PLACEHOLDER,
  statusColor: '#9E9E9E',
  barColor: '#E8E8E8',
  value: 0,
  primarySegment: 0,
  secondarySegment: 0,
  primaryColor: '#E8E8E8',
  secondaryColor: '#E8E8E8',
};

/** Pool health rows from API or empty placeholders. */
export const buildPoolHealthRows = (metrics) => {
  const pool = metrics?.poolHealth;

  return POOL_HEALTH_CONFIG.map((cfg) => {
    const raw = pool?.[cfg.key];
    if (!raw || typeof raw !== 'object') {
      return { ...cfg, ...EMPTY_POOL_ROW };
    }

    if (cfg.type === 'split') {
      const primary = Number(raw.primarySegment ?? raw.primary) || 0;
      const secondary = Number(raw.secondarySegment ?? raw.secondary) || 0;
      return {
        ...cfg,
        statusLabel: raw.statusLabel ?? EMPTY_PLACEHOLDER,
        statusDetail: raw.statusDetail ?? EMPTY_PLACEHOLDER,
        statusColor: raw.statusColor ?? '#9E9E9E',
        primarySegment: primary,
        secondarySegment: secondary,
        primaryColor: raw.primaryColor ?? '#E8E8E8',
        secondaryColor: raw.secondaryColor ?? '#E8E8E8',
      };
    }

    const value = Number(raw.value ?? raw.percentage) || 0;
    return {
      ...cfg,
      statusLabel: raw.statusLabel ?? EMPTY_PLACEHOLDER,
      statusDetail: raw.statusDetail ?? EMPTY_PLACEHOLDER,
      statusColor: raw.statusColor ?? '#9E9E9E',
      barColor: raw.barColor ?? '#E8E8E8',
      value,
    };
  });
};

/** System alerts from API or empty list. */
export const buildSystemAlerts = (metrics) => {
  const alerts = metrics?.systemAlerts ?? metrics?.alerts;
  return Array.isArray(alerts) ? alerts : [];
};

/** Impact analysis items from API or empty list. */
export const buildImpactAnalysisItems = (metrics) => {
  const items = metrics?.impactAnalysis ?? metrics?.impact;
  return Array.isArray(items) ? items : [];
};

/** Insights footer copy from API or placeholders. */
export const buildInsightsFooter = (metrics) => {
  const insights = metrics?.insights;
  const whatHappened =
    insights?.whatHappened != null && String(insights.whatHappened).trim() !== ''
      ? String(insights.whatHappened)
      : EMPTY_PLACEHOLDER;
  const nextSteps = Array.isArray(insights?.nextSteps)
    ? insights.nextSteps.filter((s) => s != null && String(s).trim() !== '')
    : [];

  return { whatHappened, nextSteps };
};
